import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import Modal from '../common/Modal/Modal';
import ScallopParameterTracking from '../ScallopParameterTracking/ScallopParameterTracking';
import '../common/SliderControls/SliderControls.css';

const MA_SLUG = 'scallop-natural-mortality-ma';

/**
 * BAS (Best Available Science) Scallop Natural Mortality - button to open the
 * Scallop Parameter Tracking modal. Uses FormContext for current MA value.
 */
export default function BasScallopNaturalMortality(
  _props: RegistryComponentProps
): React.ReactElement {
  const formContext = useFormContextOptional();
  const [showModal, setShowModal] = useState(false);

  const currentDbValue =
    (formContext?.values?.[MA_SLUG] as number | undefined) ?? 0.25;

  const handleClose = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <div
        className="ai-insights clickable"
        onClick={() => setShowModal(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowModal(true);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Explore insights from best available science for scallop natural mortality"
      >
        <div className="ai-insights-label">Insights from best available science</div>
        <div className="ai-insights-text">
          Explore how natural mortality estimates for scallops have evolved over time across different management areas.
        </div>
        <div className="ai-insights-click-hint">Click to explore detailed research</div>
      </div>

      {showModal &&
        createPortal(
          <Modal
            title="Scallop Natural Mortality - Best Available Science"
            onClose={handleClose}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '90vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px' }}
          >
            <div
              style={{
                marginBottom: '15px',
                padding: '10px',
                background: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
              }}
            >
              <p style={{ margin: 0, fontSize: '14px', color: '#0369a1' }}>
                <strong>💡 Info:</strong> This chart shows how natural mortality estimates for scallops have evolved over time across different management areas.
              </p>
            </div>
            <ScallopParameterTracking
              parameterName="Natural Mortality Rate"
              {...({ stockName: 'Atlantic Sea Scallop', previewMode: true, currentDbValue } as Record<string, unknown>)}
            />
          </Modal>,
          document.body
        )}
    </>
  );
}
